import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, AuthResult, AuthContextValue } from '../types/auth.types';
import api from '../services/api';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_KEY = 'sanctum_token';
const USER_KEY  = 'bhs_user';

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const token   = localStorage.getItem(TOKEN_KEY);
    const stored  = localStorage.getItem(USER_KEY);
    if (token && stored) {
      setUser(JSON.parse(stored) as User);
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      const u: User = {
        id:        String(data.user.id),
        email:     data.user.email,
        role:      data.user.role,
        full_name: data.user.name,
      };
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
      return { success: true };
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string; errors?: { email?: string[] } } } })
          ?.response?.data?.errors?.email?.[0] ??
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Login fehlgeschlagen';
      return { success: false, error: msg };
    }
  };

  const register = async (): Promise<AuthResult> => {
    return { success: false, error: 'Registrierung ist deaktiviert.' };
  };

  const logout = async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignorieren */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const isAdmin = (): boolean => !!user && user.role === 'admin';

  const value: AuthContextValue = {
    user,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
